import { useEffect, useMemo, useState } from "react";
import { ListChecks, Loader2, RefreshCw, Search, MessageCircle, Building2, DoorClosed, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

// WhatsApp Click-to-Chat: normalize Indonesian numbers to 62xxxxxxxxxx (no API).
function waLink(phone) {
  if (!phone) return null;
  let d = String(phone).replace(/[^0-9]/g, "");
  if (!d) return null;
  if (d.startsWith("0")) d = "62" + d.slice(1);
  else if (d.startsWith("62")) { /* already international */ }
  else if (d.startsWith("8")) d = "62" + d;
  return `https://wa.me/${d}`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function WaButton({ phone, testid }) {
  const link = waLink(phone);
  if (!link) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <a href={link} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors">
      <MessageCircle size={14} /> WhatsApp
    </a>
  );
}

export default function OwnerWaitlistPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [propId, setPropId] = useState("all");
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    setLoading(true);
    apiClient.get("/owner/waitlist").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const properties = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => { if (r.property_id) map.set(r.property_id, r.property_name || "Properti"); });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const termDigits = term.replace(/[^0-9]/g, "");
    return rows.filter((r) => {
      if (propId !== "all" && r.property_id !== propId) return false;
      if (term) {
        const nameHit = (r.name || "").toLowerCase().includes(term);
        const phoneHit = termDigits && (r.phone || "").replace(/[^0-9]/g, "").includes(termDigits);
        if (!nameHit && !phoneHit) return false;
      }
      return true;
    });
  }, [rows, propId, q]);

  const remove = async (id) => {
    if (!window.confirm("Hapus entri waiting list ini?")) return;
    setDeleting(id);
    try {
      await apiClient.delete(`/waitlist/${id}`);
      toast.success("Entri waiting list dihapus.");
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <OwnerLayout>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><ListChecks size={22} className="text-primary" /> Waiting List</h1>
          <p className="text-sm text-muted-foreground">Calon penyewa yang antre pada kamar terisi.</p>
        </div>
        <button onClick={load} data-testid="owner-waitlist-refresh"
          className="grid place-items-center w-10 h-10 rounded-full border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <span className="px-3 py-1 rounded-full bg-secondary/60 border border-border font-semibold text-sm" data-testid="owner-waitlist-count">Total {filtered.length}</span>
        <div className="flex flex-1 gap-3">
          <select value={propId} onChange={(e) => setPropId(e.target.value)} data-testid="owner-waitlist-property-filter"
            className="rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary min-w-0">
            <option value="all">Semua Properti</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="relative flex-1 min-w-0">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="owner-waitlist-search"
              placeholder="Cari nama atau nomor telepon"
              className="w-full rounded-xl border border-border bg-secondary/40 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground" data-testid="owner-waitlist-empty">
          <ListChecks size={30} className="mx-auto mb-2" /> Belum ada pendaftar waiting list.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden lg:block overflow-x-auto rounded-2xl border border-border" data-testid="owner-waitlist-table">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-secondary/50 text-left">
                <tr className="[&>th]:px-4 [&>th]:py-3 [&>th]:font-semibold [&>th]:text-muted-foreground">
                  <th>Nama</th><th>Telepon</th><th>Properti</th><th>Kamar</th><th>Waktu Daftar</th><th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((w, i) => (
                  <tr key={w.id} data-testid={`waitlist-row-${i}`} className="border-t border-border [&>td]:px-4 [&>td]:py-3">
                    <td className="font-semibold" data-testid={`waitlist-row-name-${i}`}>{w.name}</td>
                    <td data-testid={`waitlist-row-phone-${i}`}>{w.phone || "—"}</td>
                    <td>{w.property_name || "—"}</td>
                    <td>{w.room_number || "—"}</td>
                    <td>{formatDateTime(w.created_at)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <WaButton phone={w.phone} testid={`waitlist-row-wa-${i}`} />
                        <button onClick={() => remove(w.id)} disabled={deleting === w.id} data-testid={`waitlist-row-delete-${i}`}
                          className="grid place-items-center w-8 h-8 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-60">
                          {deleting === w.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile / tablet cards */}
          <div className="lg:hidden space-y-3" data-testid="owner-waitlist-cards">
            {filtered.map((w, i) => (
              <div key={w.id} data-testid={`waitlist-card-${i}`} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold">{w.name}</span>
                  <button onClick={() => remove(w.id)} disabled={deleting === w.id} data-testid={`waitlist-card-delete-${i}`}
                    className="grid place-items-center w-8 h-8 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-60">
                    {deleting === w.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </div>
                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <p className="inline-flex items-center gap-1.5"><Building2 size={13} /> {w.property_name || "—"} · <DoorClosed size={13} /> {w.room_number || "—"}</p>
                  {w.phone && <p>{w.phone}</p>}
                  <p className="inline-flex items-center gap-1.5"><Clock size={13} /> {formatDateTime(w.created_at)}</p>
                </div>
                <div className="mt-3"><WaButton phone={w.phone} testid={`waitlist-card-wa-${i}`} /></div>
              </div>
            ))}
          </div>
        </>
      )}
    </OwnerLayout>
  );
}
